import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Product, productService } from '../../services/productService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PrinterIcon,
  EyeIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { useZebraPrinter } from '../../hooks/useZebraPrinter';
import { QRPreviewModal } from '../QRPreviewModal';
import { DeleteWarningModal } from '../common/DeleteWarningModal';
import { generateProductLabelZPL } from '../../utils/zplUtils';

type SortField = 'cod_producto' | 'marca' | 'color' | 'talla' | 'precio' | 'stock' | 'codigo_proveedor' | 'rebajado';
type SortDirection = 'asc' | 'desc';

interface EditingCell {
  productId: number;
  field: keyof Product;
  value: any;
}

interface ProductTableViewProps {
  products: Product[];
  loading: boolean;
  onRefresh: () => void;
  inventoryType?: 'maestro' | 'novias';
  selectAllOnLoad?: boolean;
  sortField: string;
  sortDirection: SortDirection;
  availableBrands?: string[];
  availableColors?: string[];
  availableSizes?: string[];
  onSort: (field: SortField) => void;
  onCorrect?: (product: Product) => void;
}

export const ProductTableView: React.FC<ProductTableViewProps> = ({
  products,
  loading,
  onRefresh,
  inventoryType = 'maestro',
  selectAllOnLoad = false,
  sortField,
  sortDirection,
  availableBrands = [],
  availableColors = [],
  availableSizes = [],
  onSort,
  onCorrect
}) => {
  const { user } = useAuth();
  const isAdmin = user?.rol_usr === UserRole.ADMIN;

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [brandQuery, setBrandQuery] = useState('');
  const [colorQuery, setColorQuery] = useState('');
  const [sizeQuery, setSizeQuery] = useState('');

  // Zebra Printer
  const { printLabel, loading: printing, error: printerError } = useZebraPrinter();

  // Selection State
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [isBulkPrinting, setIsBulkPrinting] = useState(false);
  const [optimisticRebajado, setOptimisticRebajado] = useState<Record<number, boolean>>({});

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteWarnings, setDeleteWarnings] = useState<string[]>([]);
  const [isSoftDelete, setIsSoftDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset selection when products change, or select all if requested
  useEffect(() => {
    if (selectAllOnLoad && products.length > 0) {
      setSelectedProducts(products.map(p => p.id_producto));
    } else {
      setSelectedProducts([]);
    }
  }, [products, selectAllOnLoad]);

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products].filter(p => {
      // Ignorar productos placeholder de proveedores
      const code = (p.cod_producto || 'SIN_CODIGO').trim().toUpperCase();
      return !code.startsWith('PROVEEDOR-');
    });

    // Client-side sorting removed in favor of Server-side sorting

    return filtered;
  }, [products]);

  const filteredBrands = useMemo(() => {
    if (brandQuery === '') return availableBrands;
    return availableBrands.filter((brand) =>
      brand.toLowerCase().includes(brandQuery.toLowerCase())
    );
  }, [brandQuery, availableBrands]);

  const filteredColors = useMemo(() => {
    if (colorQuery === '') return availableColors;
    return availableColors.filter((color) =>
      color.toLowerCase().includes(colorQuery.toLowerCase())
    );
  }, [colorQuery, availableColors]);

  const filteredSizes = useMemo(() => {
    if (sizeQuery === '') return availableSizes;
    return availableSizes.filter((size) =>
      size.toLowerCase().includes(sizeQuery.toLowerCase())
    );
  }, [sizeQuery, availableSizes]);

  const handleSort = (field: SortField) => {
    onSort(field);
  };

  const handleCellClick = (product: Product, field: keyof Product) => {
    if (!isAdmin) return;
    if (['id_producto', 'created_at', 'activo', 'codigo_proveedor'].includes(field)) return;

    setEditingCell({
      productId: product.id_producto,
      field,
      value: product[field]
    });
    if (field === 'marca') setBrandQuery('');
    if (field === 'color') setColorQuery('');
    if (field === 'talla') setSizeQuery('');
  };

  const handleCellChange = (value: any) => {
    if (!editingCell) return;
    if (editingCell.field === 'stock') {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        const currentProduct = products.find(p => p.id_producto === editingCell.productId);
        const isGenericOrAccessory = currentProduct?.codigo_proveedor === 'GEN' || currentProduct?.cod_producto?.toUpperCase().includes('CUBRE');
        
        if (!isGenericOrAccessory && numValue !== 0 && numValue !== 1) {
          toast.error('Para vestidos y prendas únicas, el stock solo puede ser 0 o 1');
          return;
        }
      }
    }
    setEditingCell({ ...editingCell, value });
  };

  const normalizeValue = (field: keyof Product, value: string): string => {
    if (field === 'color') {
      // Check if exists in availableColors (case-insensitive)
      const match = availableColors.find(c => c.toLowerCase() === value.toLowerCase());
      if (match) return match;
      return value.toUpperCase(); // Default new colors to UPPERCASE if not found
    }
    if (field === 'talla') {
      const match = availableSizes.find(s => s.toLowerCase() === value.toLowerCase());
      if (match) return match;
      return value.toUpperCase(); // Default new sizes to UPPERCASE
    }
    return value;
  };

  const handleSaveCell = async (overrideValue?: any) => {
    if (!editingCell) return;
    let updatedValue = overrideValue !== undefined ? overrideValue : editingCell.value;
    let extraUpdates = {};

    if (editingCell.field === 'stock') {
      const newStock = parseInt(updatedValue);
      if (!isNaN(newStock)) {
        const isActive = newStock > 0;
        extraUpdates = { activo: isActive };
      }
    } else if (editingCell.field === 'marca') {
      // Strict Validation for Brand
      if (!availableBrands.includes(updatedValue)) {
        toast.error('La marca seleccionada no es válida. Debe elegir una de la lista.');
        return;
      }
    } else if (editingCell.field === 'color' || editingCell.field === 'talla') {
      // Normalize Value
      updatedValue = normalizeValue(editingCell.field, updatedValue);
    }

    setEditingCell(null);
    try {
      // Determine type: if product has specific type (from 'all' query), use it. Else fallback to prop.
      const currentProduct = products.find(p => p.id_producto === editingCell.productId);
      const specificType = currentProduct?.inventoryType || inventoryType;

      const updateData: any = { [editingCell.field]: updatedValue, ...extraUpdates, type: specificType };

      // If updating brand, we set flag to regenerate provider code logic? 
      // Actually backend updateProduct will regen provider code if marca changes. 
      // We rely on backend logic.

      const result = await productService.updateProduct(editingCell.productId, updateData);

      // Check if backend detected an inventory match (pre-order swap)
      if (result?.inventoryMatch && result?.matchedProduct) {
        const mp = result.matchedProduct;
        const doSwap = window.confirm(
          `(!PRODUCTO ENCONTRADO EN INVENTARIO)\n\n` +
          `El producto ${mp.cod_producto} ${mp.color} ${mp.talla} ya existe en inventario con stock disponible.\n\n` +
          `¿Deseas intercambiar?\n` +
          `• El producto del inventario se asociará a la venta\n` +
          `• El pre-order se convertirá en inventario disponible`
        );
        if (doSwap) {
          await productService.updateProduct(editingCell.productId, { ...updateData, confirmSwap: true });
          toast.success('Pre-order intercambiado exitosamente');
        } else {
          toast.success('Corrección cancelada');
        }
      } else if (result?.swapped) {
        toast.success(result.message || 'Pre-order intercambiado exitosamente');
      } else {
        toast.success('Producto actualizado');
      }
      onRefresh();
    } catch (error) {
      toast.error('Error al actualizar producto');
      console.error(error);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  const handleSaveAndMoveNext = async (overrideValue?: any) => {
    if (!editingCell) return;
    const currentProductId = editingCell.productId;
    const currentField = editingCell.field;
    let currentValue = overrideValue !== undefined ? overrideValue : editingCell.value;
    let extraUpdates = {};

    if (currentField === 'stock') {
      const newStock = parseInt(currentValue);
      if (!isNaN(newStock)) {
        const isActive = newStock > 0;
        extraUpdates = { activo: isActive };
      }
    } else if (currentField === 'marca') {
      if (!availableBrands.includes(currentValue)) {
        toast.error('La marca seleccionada no es válida. Debe elegir una de la lista.');
        return;
      }
    } else if (currentField === 'color' || currentField === 'talla') {
      currentValue = normalizeValue(currentField, currentValue);
    }

    const currentIndex = filteredAndSortedProducts.findIndex((p) => p.id_producto === currentProductId);
    if (currentIndex < filteredAndSortedProducts.length - 1) {
      const nextProduct = filteredAndSortedProducts[currentIndex + 1];
      setEditingCell({
        productId: nextProduct.id_producto,
        field: currentField,
        value: nextProduct[currentField]
      });
      if (currentField === 'marca') setBrandQuery('');
      if (currentField === 'color') setColorQuery('');
      if (currentField === 'talla') setSizeQuery('');
    } else {
      setEditingCell(null);
    }
    try {
      // Find current product to get type
      const activeProduct = filteredAndSortedProducts.find(p => p.id_producto === currentProductId);
      const specificType = activeProduct?.inventoryType || inventoryType;

      const updateData: any = { [currentField]: currentValue, ...extraUpdates, type: specificType };
      const result = await productService.updateProduct(currentProductId, updateData);

      // Check if backend detected an inventory match (pre-order swap)
      if (result?.inventoryMatch && result?.matchedProduct) {
        const mp = result.matchedProduct;
        const doSwap = window.confirm(
          `(!PRODUCTO ENCONTRADO EN INVENTARIO)\n\n` +
          `El producto ${mp.cod_producto} ${mp.color} ${mp.talla} ya existe en inventario con stock disponible.\n\n` +
          `¿Deseas intercambiar?\n` +
          `• El producto del inventario se asociará a la venta\n` +
          `• El pre-order se convertirá en inventario disponible`
        );
        if (doSwap) {
          await productService.updateProduct(currentProductId, { ...updateData, confirmSwap: true });
          toast.success('Pre-order intercambiado exitosamente');
        } else {
          toast.success('Corrección cancelada');
        }
      } else if (result?.swapped) {
        toast.success(result.message || 'Pre-order intercambiado exitosamente');
      }
      onRefresh();
    } catch (error) {
      toast.error('Error al actualizar');
      console.error(error);
    }
  };

  const getEffectiveProduct = (product: Product) => {
    const optimisticVal = optimisticRebajado[product.id_producto];
    if (optimisticVal !== undefined) {
      return { ...product, rebajado: optimisticVal };
    }
    return product;
  };

  const handlePrintQR = async (p: Product) => {
    const product = getEffectiveProduct(p);
    let qrValue = product.qr_code_value;
    if (!qrValue) {
      try {
        await productService.updateProduct(product.id_producto, { generate_qr: true, type: product.inventoryType });
        toast.success('Código QR generado y guardado');
        onRefresh();
        return;
      } catch (e) {
        console.error(e);
        toast.error('Error al guardar código QR generado');
        return;
      }
    }

    // Fetch variants
    const { sizes, colors } = await productService.getVariants(product.cod_producto || '');

    const zpl = generateProductLabelZPL(product, {
      noteNumber: product.saleFolio,
      availableSizes: sizes,
      availableColors: colors
    });

    const success = await printLabel(zpl);
    if (success) { 
      toast.success('Enviado a imprimir'); 
    } else { 
      // Use printerError from hook
      toast.error(printerError || 'Error al imprimir. Verifica el botón Configurar USB.'); 
    }
  };

  const handlePreviewQR = async (p: Product) => {
    const product = getEffectiveProduct(p);
    let qrValue = product.qr_code_value;
    if (!qrValue) {
      try {
        await productService.updateProduct(product.id_producto, { generate_qr: true, type: product.inventoryType });
        onRefresh();
      } catch (e) {
        console.error(e);
        toast.error('Error al generar QR');
        return;
      }
    }

    setPreviewProduct(product);
    setShowPreviewModal(true);
  };

  const handleBulkPrintSelected = async () => {
    if (selectedProducts.length === 0) return;
    setIsBulkPrinting(true);
    try {
      const selected = products.filter(p => selectedProducts.includes(p.id_producto));

      // 1. Check for missing QRs and trigger generation if needed
      const missingQR = selected.filter(p => !p.qr_code_value);
      if (missingQR.length > 0) {
        toast.loading(`Generando ${missingQR.length} códigos QR faltantes...`, { id: 'generating-qr' });
        // Generate in parallel
        await Promise.all(missingQR.map(p =>
          productService.updateProduct(p.id_producto, { generate_qr: true, type: p.inventoryType })
        ));
        toast.dismiss('generating-qr');
        toast.success(`Códigos generados. Preparando impresión...`);
        onRefresh();
      }

      let totalZpl = '';

      // Map to promises to fetch variants in parallel (or sequential if preferred for load)
      // Group unique codes to avoid redundant calls
      const uniqueCodes = Array.from(new Set(selected.map(p => p.cod_producto).filter(Boolean))) as string[];
      const codeVariantsMap: Record<string, { sizes: string[], colors: string[] }> = {};

      await Promise.all(uniqueCodes.map(async (code) => {
        codeVariantsMap[code] = await productService.getVariants(code);
      }));

      selected.forEach(rawP => {
        const p = getEffectiveProduct(rawP);
        const variants = p.cod_producto ? codeVariantsMap[p.cod_producto] : { sizes: [], colors: [] };
        totalZpl += generateProductLabelZPL(p, {
          noteNumber: p.saleFolio,
          availableSizes: variants?.sizes || [],
          availableColors: variants?.colors || []
        });
      });

      if (!totalZpl) { toast.error('No hay productos seleccionados'); return; }

      const success = await printLabel(totalZpl);
      if (success) {
        toast.success(`Enviado a imprimir ${selected.length} etiquetas`);
        setSelectedProducts([]);
      } else {
        toast.error('Error al imprimir. Verifica conexión USB.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al imprimir');
    } finally {
      setIsBulkPrinting(false);
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    if (sortDirection === 'asc') { return React.createElement(ArrowUpIcon, { className: 'w-4 h-4 inline ml-1' }); }
    return React.createElement(ArrowDownIcon, { className: 'w-4 h-4 inline ml-1' });
  };

  const renderCell = (product: Product, field: keyof Product) => {
    const isEditing = editingCell?.productId === product.id_producto && editingCell?.field === field;
    const value = product[field];

    if (isEditing) {
      if (field === 'marca') {
        return (
          <div className="relative min-w-[200px]" onClick={(e) => e.stopPropagation()}>
            <Combobox value={editingCell.value} onChange={(val) => handleCellChange(val)}>
              <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                <Combobox.Input
                  className="w-full border-2 border-blue-500 rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none font-medium"
                  displayValue={(brand: string) => brand}
                  onChange={(event) => setBrandQuery(event.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      let finalVal = editingCell.value;
                      if (brandQuery.trim() !== '') {
                        const normalizedQuery = brandQuery.toLowerCase().replace(/\s+/g, '');
                        const exactMatch = filteredBrands.find(b => b.toLowerCase().replace(/\s+/g, '') === normalizedQuery);
                        if (exactMatch) {
                          finalVal = exactMatch;
                        } else if (filteredBrands.length > 0) {
                          finalVal = filteredBrands[0];
                        }
                      }
                      if (availableBrands.includes(finalVal)) {
                        handleSaveAndMoveNext(finalVal);
                      } else {
                        toast.error('La marca seleccionada no es válida.');
                      }
                    }
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ArrowDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setBrandQuery('')}
              >
                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                  {filteredBrands.length === 0 && brandQuery !== '' ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      No existe esa marca registrada
                    </div>
                  ) : (
                    filteredBrands.map((brand) => (
                      <Combobox.Option
                        key={brand}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'
                          }`
                        }
                        value={brand}
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                }`}
                            >
                              {brand}
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'
                                  }`}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </Combobox>
            <div className="flex justify-end mt-2 space-x-2">
              <button onClick={(e) => { e.stopPropagation(); handleSaveCell(); }} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0" title="Guardar"><CheckIcon className="w-5 h-5" /></button>
              <button onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-shrink-0" title="Cancelar"><XMarkIcon className="w-5 h-5" /></button>
            </div>
          </div>
        )
      }

      if (field === 'color' || field === 'talla') {
        const query = field === 'color' ? colorQuery : sizeQuery;
        const setQuery = field === 'color' ? setColorQuery : setSizeQuery;
        const filteredList = field === 'color' ? filteredColors : filteredSizes;

        return (
          <div className="relative min-w-[200px]" onClick={(e) => e.stopPropagation()}>
            <Combobox value={editingCell.value} onChange={(val) => handleCellChange(val)} nullable>
              <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                <Combobox.Input
                  className="w-full border-2 border-blue-500 rounded-lg py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none font-medium"
                  displayValue={(item: string) => item}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    handleCellChange(event.target.value);
                  }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      let finalVal = editingCell.value;
                      if (query.trim() !== '') {
                        const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
                        const exactMatch = filteredList.find(c => c.toLowerCase().replace(/\s+/g, '') === normalizedQuery);
                        if (exactMatch) {
                          finalVal = exactMatch;
                        } else if (filteredList.length > 0) {
                          finalVal = filteredList[0];
                        } else {
                          finalVal = query; // allow custom
                        }
                      }
                      handleSaveAndMoveNext(finalVal);
                    }
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ArrowDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery('')}
              >
                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                  {filteredList.length === 0 && query !== '' ? (
                    <Combobox.Option
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'
                        }`
                      }
                      value={query}
                    >
                      <span className="block truncate font-medium">Create "{query}"</span>
                    </Combobox.Option>
                  ) : (
                    filteredList.map((item) => (
                      <Combobox.Option
                        key={item}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'
                          }`
                        }
                        value={item}
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                }`}
                            >
                              {item}
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'
                                  }`}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </Combobox>
            <div className="flex justify-end mt-2 space-x-2">
              <button onClick={(e) => { e.stopPropagation(); handleSaveCell(); }} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0" title="Guardar"><CheckIcon className="w-5 h-5" /></button>
              <button onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-shrink-0" title="Cancelar"><XMarkIcon className="w-5 h-5" /></button>
            </div>
          </div>
        )
      }

      return (
        <div className="flex items-center space-x-2 min-w-[200px]">
          {field === 'precio' ? (
            <input
              type="number" step="0.01" value={editingCell.value || ''}
              onChange={(e) => handleCellChange(parseFloat(e.target.value))}
              autoFocus className="flex-1 px-3 py-2 bg-white border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-900 font-medium"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSaveAndMoveNext(); } if (e.key === 'Escape') handleCancelEdit(); }}
            />
          ) : field === 'stock' ? (
            <input
              type="number" value={editingCell.value === 0 ? '0' : (editingCell.value || '')}
              onChange={(e) => handleCellChange(parseInt(e.target.value) || 0)}
              autoFocus className="flex-1 px-3 py-2 bg-white border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-900 font-medium"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSaveAndMoveNext(); } if (e.key === 'Escape') handleCancelEdit(); }}
            />
          ) : (
            <input
              type="text" value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              autoFocus className="flex-1 px-3 py-2 bg-white border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-900 font-medium"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSaveAndMoveNext(); } if (e.key === 'Escape') handleCancelEdit(); }}
            />
          )}
          <button onClick={(e) => { e.stopPropagation(); handleSaveCell(); }} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0" title="Guardar"><CheckIcon className="w-5 h-5" /></button>
          <button onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-shrink-0" title="Cancelar"><XMarkIcon className="w-5 h-5" /></button>
        </div>
      );
    }
    if (field === 'precio') { return <span className="font-semibold text-gray-900">{value ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value as number) : '-'}</span>; }
    if (field === 'stock') {
      const stock = (value as number) || 0; const isEmpty = stock === 0;
      return <span className={`font-bold text-lg ${isEmpty ? 'text-red-600' : 'text-green-600'}`}>{stock}</span>;
    }
    if (field === 'activo') {
      const getBadgeClass = (baseColor: string) => `px-2 py-1 rounded-md text-xs font-bold leading-none bg-${baseColor}-100 text-${baseColor}-800 border border-${baseColor}-200 shadow-sm`;

      if (product.in_transit) return <span className={getBadgeClass('amber')}>En Tránsito</span>;
      if (product.pre_order) {
        if (product.sellerRole === UserRole.VENDEDORA_EN_LINEA) {
          return <span className={getBadgeClass('purple')}>VL-PRE</span>;
        }
        
        // Nueva lógica de estados automáticos para Pre-orders
        if (product.invoice_id && !product.in_transit) {
            return <span className={getBadgeClass('emerald')}>RECIBIDO</span>;
        }
        return <span className={getBadgeClass('yellow')}>PENDIENTE</span>;
      }
      return <span className={getBadgeClass(value ? 'green' : 'red')}>{value ? 'Activo' : 'Inactivo'}</span>;
    }
    return <span className="text-gray-900 font-medium">{value?.toString() || <span className="text-gray-400">-</span>}</span>;
  };

  const handleDeleteClick = async (product: Product) => {
    try {
      const res = await productService.checkProductDependencies(product.id_producto, product.inventoryType);
      if (res.success && res.data) {
        const warnings: string[] = [];
        let soft = false;

        if (res.data.hasSales) {
          warnings.push(`Este producto tiene ${res.data.salesCount} operación(es) vinculada(s) (Ej. Ventas/Kardex).`);
          warnings.push('Eliminarlo puede afectar la consistencia histórica. Solo se realizará una desactivación (Soft Delete).');
          soft = true;
        } else {
          warnings.push('Este producto NO tiene ventas ni tickets registrados.');
          warnings.push('Se eliminará permanentemente de la base de datos (Hard Delete).');
        }

        if (res.data.hasInvoice) {
          warnings.push(`Pertenece a la compra de origen (Invoice): ${res.data.invoiceId}`);
        }

        if (res.data.isPreOrder) {
          warnings.push('Actualmente está marcado como un Pre-Order activo o pendiente.');
        }

        warnings.push('🚨 IMPORTANTE: Si este producto ya cuenta con una etiqueta física (código de barras) impresa, por favor destruyela manualmente para evitar errores de escaneo.');

        setDeleteWarnings(warnings);
        setIsSoftDelete(soft);
        setProductToDelete(product);
        setDeleteModalOpen(true);
      }
    } catch (error) {
      toast.error('Error al verificar dependencias del producto');
    }
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    setIsDeleting(true);
    try {
      const res = await productService.deleteProduct(productToDelete.id_producto, productToDelete.inventoryType);
      toast.success(res.message || 'Producto eliminado exitosamente');
      setDeleteModalOpen(false);
      onRefresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar el producto');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Bulk Action Bar */}
      {selectedProducts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center animate-fade-in shadow-sm w-full mb-4">
          <span className="font-medium text-blue-800">
            {selectedProducts.length} productos seleccionados
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkPrintSelected}
              disabled={isBulkPrinting}
              className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors flex items-center shadow-sm font-medium"
            >
              <PrinterIcon className="w-5 h-5 mr-2" />
              {isBulkPrinting ? 'Imprimiendo...' : 'Imprimir Etiquetas'}
            </button>
            <button
              onClick={() => setSelectedProducts([])}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto bg-white rounded-lg shadow ring-1 ring-black ring-opacity-5">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={filteredAndSortedProducts.length > 0 && selectedProducts.length === filteredAndSortedProducts.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const method2 = filteredAndSortedProducts.map(p => p.id_producto);
                      setSelectedProducts(Array.from(new Set([...selectedProducts, ...method2])));
                    } else {
                      const visibleIds = filteredAndSortedProducts.map(p => p.id_producto);
                      setSelectedProducts(selectedProducts.filter(id => !visibleIds.includes(id)));
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
              </th>
              <th onClick={() => handleSort('cod_producto')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Código</span>{renderSortIcon('cod_producto')}</div>
              </th>
              {/* Nueva columna para mostrar tipo de inventario */}
              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Inventario
              </th>

              {true && (
                <>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Invoice
                  </th>
                </>
              )}

              <th onClick={() => handleSort('codigo_proveedor')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Código Proveedor</span>{renderSortIcon('codigo_proveedor')}</div>
              </th>
              <th onClick={() => handleSort('marca')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Marca</span>{renderSortIcon('marca')}</div>
              </th>
              <th onClick={() => handleSort('color')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Color</span>{renderSortIcon('color')}</div>
              </th>
              <th onClick={() => handleSort('talla')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Talla</span>{renderSortIcon('talla')}</div>
              </th>
              <th onClick={() => handleSort('precio')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Precio</span>{renderSortIcon('precio')}</div>
              </th>
              <th onClick={() => handleSort('rebajado')} className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center justify-center space-x-2"><span>Rebajado</span>{renderSortIcon('rebajado')}</div>
              </th>
              <th onClick={() => handleSort('stock')} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="flex items-center space-x-2"><span>Stock</span>{renderSortIcon('stock')}</div>
              </th>

              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Estado</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedProducts.length === 0 ? (
              <tr className="bg-white">
                <td colSpan={11} className="px-6 py-8 text-center text-gray-500 bg-white">
                  {loading ? '' : 'No se encontraron productos'}
                </td>
              </tr>
            ) : (
              filteredAndSortedProducts.map((product) => (
                <tr key={product.id_producto} className={`bg-white hover:bg-gray-50 transition-colors ${selectedProducts.includes(product.id_producto) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id_producto)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts([...selectedProducts, product.id_producto]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(id => id !== product.id_producto));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                  </td>
                  <td onClick={() => handleCellClick(product, 'cod_producto')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'cod_producto')}</td>
                  <td className="px-3 py-2 text-sm">
                    {product.inventoryType === 'novias' ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-none font-semibold rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                        Novias
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-none font-semibold rounded-md bg-blue-100 text-blue-800 border border-blue-200">
                        Maestro
                      </span>
                    )}
                  </td>

                  {true && (
                    <>
                      <td className="px-3 py-2 text-sm font-medium text-gray-600">
                        {(() => {
                          const saleDate = (product as any).preOrderDetails?.date;
                          const dateToShow = saleDate || product.created_at;
                          return dateToShow ? new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateToShow)) : '-';
                        })()}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {product.invoice_id ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-gray-800">{product.invoice_id}</span>
                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded shadow-sm w-max ${product.in_transit ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'}`}>
                              {product.in_transit ? 'EN TRÁNSITO' : 'RECIBIDO'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">Sin asignar</span>
                        )}
                      </td>
                    </>
                  )}

                  <td onClick={() => handleCellClick(product, 'codigo_proveedor')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors group">
                    <div className="flex items-center justify-between">{renderCell(product, 'codigo_proveedor')}
                      {!editingCell && <PencilIcon className="w-4 h-4 ml-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </div>
                  </td>
                  <td onClick={() => handleCellClick(product, 'marca')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'marca')}</td>
                  <td onClick={() => handleCellClick(product, 'color')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'color')}</td>
                  <td onClick={() => handleCellClick(product, 'talla')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'talla')}</td>
                  <td onClick={() => handleCellClick(product, 'precio')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'precio')}</td>
                  <td className="px-3 py-2 text-sm text-center">
                    <input
                      type="checkbox"
                      checked={optimisticRebajado[product.id_producto] !== undefined ? optimisticRebajado[product.id_producto] : !!product.rebajado}
                      onChange={async (e) => {
                        if (!isAdmin) return;
                        const newVal = e.target.checked;

                        // Optimistic Update
                        setOptimisticRebajado(prev => ({ ...prev, [product.id_producto]: newVal }));

                        try {
                          await productService.updateProduct(product.id_producto, {
                            activo: product.activo || false,
                            type: product.inventoryType,
                            // @ts-ignore
                            rebajado: newVal
                          });
                          toast.success(newVal ? 'Marcado como Rebajado' : 'Desmarcado Rebajado', { duration: 1000 });
                          // Remove from optimistic state after successful background refresh (optional, or just let refresh overwrite)
                          // We don't await onRefresh to keep UI snappy, but we trigger it
                          onRefresh();
                        } catch (err) {
                          console.error(err);
                          toast.error('Error al actualizar');
                          // Revert optimistic update
                          setOptimisticRebajado(prev => {
                            const next = { ...prev };
                            delete next[product.id_producto];
                            return next;
                          });
                        }
                      }}
                      disabled={!isAdmin}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4 cursor-pointer disabled:opacity-50"
                    />
                  </td>
                  <td onClick={() => handleCellClick(product, 'stock')} className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors">{renderCell(product, 'stock')}</td>

                  <td className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-1">
                      {renderCell(product, 'activo')}
                      <div className="flex bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
                        <button onClick={(e) => { e.stopPropagation(); handlePreviewQR(product); }} className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors p-1.5 rounded-l" title="Ver QR"><EyeIcon className="w-4 h-4" /></button>
                        <div className="w-px bg-gray-200"></div>
                        <button onClick={(e) => { e.stopPropagation(); handlePrintQR(product); }} className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors p-1.5" title="Imprimir QR" disabled={printing}><PrinterIcon className="w-4 h-4" /></button>
                        <div className="w-px bg-gray-200"></div>
                        {isAdmin && product.pre_order && onCorrect && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); onCorrect(product); }}
                              className="text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 transition-colors p-1.5 font-bold text-xs"
                              title="Corregir datos/Editar"
                            >
                              EDITAR
                            </button>
                            <div className="w-px bg-gray-200"></div>
                          </>
                        )}
                        {/* Botón de entrega manual eliminado */}

                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(product); }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors p-1.5 rounded-r"
                            title="Eliminar Producto"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <SwitchInventoryButton
                            product={product}
                            onSwitch={async () => {
                              try {
                                const target = product.inventoryType === 'novias' ? 'maestro' : 'novias';
                                await productService.switchInventoryType(product.id_producto, target);
                                toast.success('Producto movido exitosamente');
                                onRefresh();
                              } catch (error: any) {
                                toast.error('Error al mover producto: ' + (error.response?.data?.message || error.message));
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPreviewModal && previewProduct && (
        <QRPreviewModal
          product={previewProduct}
          noteNumber={previewProduct.saleFolio}
          onClose={() => setShowPreviewModal(false)}
        />
      )}

      {productToDelete && (
        <DeleteWarningModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title={`Eliminar Producto: ${productToDelete.cod_producto}`}
          isSoftDelete={isSoftDelete}
          isLoading={isDeleting}
          warnings={deleteWarnings}
        />
      )}
    </>
  );
};

const SwitchInventoryButton = ({ product, onSwitch }: { product: Product, onSwitch: () => void }) => {
  const isNovias = product.inventoryType === 'novias';
  const targetLabel = isNovias ? 'Maestro' : 'Novias';


  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (window.confirm(`¿Mover producto "${product.cod_producto}" al inventario ${targetLabel}?\n\nSe mantendrá el historial de ventas.`)) {
          onSwitch();
        }
      }}
      className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-indigo-600`}
      title={`Mover a inventario ${targetLabel}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    </button>
  );
};

export default ProductTableView;
